/**
 * Application-wide default values and constants
 * 
 * This file centralizes all hardcoded values used across the application.
 * Organize by domain/feature for easy discovery and maintenance.
 */

// =============================================================================
// URLs
// =============================================================================

/**
 * Default URLs used throughout the application
 */
export const DEFAULT_URLS = {
  PRIVACY_POLICY: 'https://renewmyday.com/privacy-cookie-statement',
  GRAPH_ME_ENDPOINT: 'https://graph.microsoft.com/v1.0/me',
} as const;

// =============================================================================
// User Settings
// =============================================================================

/**
 * Default user settings for new or unconfigured users
 */
export const DEFAULT_USER = {
  TIMEZONE: 'Europe/Amsterdam',
  LANGUAGE: 'en',
} as const;

/**
 * Default mood settings
 */
export const DEFAULT_MOOD = {
  VALUE: 5,
  MIN: 1,
  MAX: 10,
} as const;

// =============================================================================
// UI Timeouts & Delays (in milliseconds)
// =============================================================================

/**
 * UI notification and feedback timeouts
 */
export const UI_TIMEOUTS = {
  /** Duration to show success messages before auto-dismissing */
  SUCCESS_MESSAGE: 3000,
  /** Duration to show error messages before auto-dismissing */
  ERROR_MESSAGE: 5000,
  /** Small delay for focus operations after state changes */
  FOCUS_DELAY: 100,
  /** Very small delay for immediate focus (new session) */
  FOCUS_DELAY_SHORT: 20,
  /** Delay before starting new session operations */
  NEW_SESSION_DELAY: 50,
  /** Default animation delay for markdown rendering */
  ANIMATION_DELAY: 300,
} as const;

// =============================================================================
// Pagination & Limits
// =============================================================================

/**
 * Pagination settings for various lists
 */
export const PAGINATION = {
  /** Number of chat sessions per page */
  CHAT_SESSIONS_PAGE_SIZE: 20,
  /** Default limit for fetching chat sessions */
  CHAT_SESSIONS_DEFAULT_LIMIT: 50,
  /** Default limit for fetching messages */
  MESSAGES_DEFAULT_LIMIT: 50,
  /** Default limit for latest messages context */
  LATEST_MESSAGES_LIMIT: 10,
  /** Default limit for admin sessions list */
  ADMIN_SESSIONS_DEFAULT_LIMIT: 10,
  /** Default limit for admin feedbacks list */
  ADMIN_FEEDBACKS_DEFAULT_LIMIT: 100,
  /** Max limit for admin API calls */
  ADMIN_MAX_LIMIT: 1000,
} as const;

/**
 * Performance limits for UI rendering
 */
export const PERFORMANCE_LIMITS = {
  /** Maximum messages to keep in memory for chat performance */
  MAX_MESSAGES_IN_MEMORY: 50,
  /** Default height estimate per message for virtualization */
  MESSAGE_ITEM_HEIGHT: 100,
  /** Default container height for virtualized lists */
  VIRTUALIZED_CONTAINER_HEIGHT: 400,
} as const;

// =============================================================================
// Validation Limits
// =============================================================================

/**
 * Input field validation limits
 */
export const VALIDATION = {
  /** Maximum length for display name */
  DISPLAY_NAME_MAX_LENGTH: 100,
  /** Maximum length for email address */
  EMAIL_MAX_LENGTH: 255,
} as const;

// =============================================================================
// Service Configuration
// =============================================================================

/**
 * Health service configuration
 */
export const HEALTH_SERVICE = {
  /** Interval between health checks (3 minutes) */
  CHECK_INTERVAL: 3 * 60 * 1000,
  /** Idle threshold before triggering pre-warm (5 minutes) */
  IDLE_THRESHOLD: 5 * 60 * 1000,
  /** Timeout for health check requests */
  REQUEST_TIMEOUT: 10000,
  /** Max retry attempts for health checks */
  MAX_RETRY_ATTEMPTS: 2,
} as const;

/**
 * MSAL service configuration
 */
export const MSAL_SERVICE = {
  /** Max retry attempts for initialization */
  MAX_RETRIES: 3,
  /** Delay between retry attempts (base delay, multiplied by attempt number) */
  RETRY_DELAY: 1000,
  /** Delay before initiating force login after cleanup */
  FORCE_LOGIN_DELAY: 500,
} as const;

/**
 * Admin service configuration
 */
export const ADMIN_SERVICE = {
  /** Request timeout for admin operations */
  TIMEOUT: 30000,
  /** Max retry attempts for admin operations */
  MAX_RETRY_ATTEMPTS: 3,
} as const;

// =============================================================================
// SignalR Configuration
// =============================================================================

/**
 * SignalR default configuration values
 */
export const SIGNALR_DEFAULTS = {
  /** Message timeout for test environments */
  MESSAGE_TIMEOUT_TEST: 5000,
  /** Message timeout for production */
  MESSAGE_TIMEOUT_PROD: 30000,
  /** Max cache size for test environments */
  MAX_CACHE_SIZE_TEST: 100,
  /** Max cache size for production */
  MAX_CACHE_SIZE_PROD: 1000,
  /** Heartbeat interval for test environments */
  HEARTBEAT_INTERVAL_TEST: 5000,
  /** Heartbeat interval for production */
  HEARTBEAT_INTERVAL_PROD: 30000,
} as const;

// =============================================================================
// Type Exports
// =============================================================================

export type DefaultUrls = typeof DEFAULT_URLS;
export type DefaultUser = typeof DEFAULT_USER;
export type DefaultMood = typeof DEFAULT_MOOD;
export type UITimeouts = typeof UI_TIMEOUTS;
export type PaginationConfig = typeof PAGINATION;
export type PerformanceLimits = typeof PERFORMANCE_LIMITS;
export type ValidationConfig = typeof VALIDATION;
export type HealthServiceConfig = typeof HEALTH_SERVICE;
export type MsalServiceConfig = typeof MSAL_SERVICE;
export type AdminServiceConfig = typeof ADMIN_SERVICE;
export type SignalRDefaults = typeof SIGNALR_DEFAULTS;